import Placeholder from "@/components/Placeholder";
import { MapPin } from "lucide-react";

const cities = ["Pune", "PCMC", "Mumbai", "Thane", "Kolhapur", "Solapur"];

/**
 * `images` (optional) — provide real assets for this block. Keys:
 *   main:  hero landscape image (16/10)
 *   photo1, photo2, photo3: small square thumbnails
 * Any missing key falls back to a labelled placeholder.
 */
function FlagshipBlock({ id, name, tagline, description, mirror, cityChips, images = {}, mainAspect = "16/10", mainFit = "cover" }) {
  const renderMain = () =>
    images.main ? (
      <div className="relative w-full rounded-2xl overflow-hidden border border-[var(--ee-border)] bg-black" style={{ aspectRatio: mainAspect }}>
        <img
          data-testid={`${id}-hero-image`}
          src={images.main}
          alt={`${name} — main`}
          loading="lazy"
          className={`absolute inset-0 w-full h-full ${mainFit === "contain" ? "object-contain" : "object-cover"}`}
          draggable={false}
        />
      </div>
    ) : (
      <Placeholder
        testId={`${id}-hero-image`}
        note={`INSERT MAIN PHOTO — ${name} hero image (landscape 16:9).`}
        aspect={mainAspect}
      />
    );

  const renderThumb = (key, index) =>
    images[key] ? (
      <div key={key} className="relative w-full aspect-square rounded-lg overflow-hidden border border-[var(--ee-border)]">
        <img
          data-testid={`${id}-${key}`}
          src={images[key]}
          alt={`${name} photo ${index}`}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />
      </div>
    ) : (
      <Placeholder key={key} testId={`${id}-${key}`} note={`${name.toUpperCase()} PHOTO ${index}`} aspect="1/1" />
    );

  return (
    <div
      id={id}
      data-testid={`legacy-${id}`}
      className={`grid lg:grid-cols-2 gap-10 items-center fade-up ${
        mirror ? "lg:[&>div:first-child]:order-2" : ""
      }`}
    >
      <div>
        {renderMain()}
        <div className="grid grid-cols-3 gap-3 mt-3">
          {renderThumb("photo1", 1)}
          {renderThumb("photo2", 2)}
          {renderThumb("photo3", 3)}
        </div>
      </div>

        <div>
          <div className="eyebrow mb-4">Flagship Event</div>
          <h3 className="font-display text-4xl lg:text-5xl leading-tight">
            {name} <span className="gold-text-gradient italic">— {tagline}</span>
          </h3>
          <p className="mt-5 text-neutral-300 leading-relaxed max-w-lg">
            {description}
          </p>

          {cityChips && (
            <div className="mt-6 flex flex-wrap gap-2">
              {cities.map((c) => (
                <span
                  key={c}
                  className="text-xs px-3 py-1.5 rounded-full border border-[var(--ee-border)] text-neutral-300 flex items-center gap-1.5"
                >
                  <MapPin size={12} className="text-[var(--ee-gold)]" /> {c}
                </span>
              ))}
            </div>
          )}
        </div>
    </div>
  );
}

export default function Legacy() {
  return (
    <section id="legacy" data-testid="legacy-section" className="section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center fade-up max-w-2xl mx-auto">
          <div className="eyebrow justify-center mb-4">Our Legacy</div>
          <h2 className="section-title">
            Two flagships.{" "}
            <span className="gold-text-gradient italic">One studio.</span>
          </h2>
          <p className="mt-4 text-neutral-400">
            {/* Single short dash keeps the sentence lighter and more premium. */}
            The events that put Elite Eventz on the map – and defined how we
            approach every show since.
          </p>
        </div>

        <div className="mt-20 space-y-28">
          <FlagshipBlock
            id="petrichor"
            name="Petrichor"
            tagline="The Signature Season"
            description="Petrichor

Where Music, Purpose, and Community Come Together

Petrichor is a unique musical experience organized by the AIRA Foundation and professionally managed by Elite Eventz. Created with the vision of bringing people together through the universal language of music, the event celebrates creativity, community and meaningful impact. Every performance is carefully curated to create an atmosphere where entertainment meets purpose, leaving audiences with memories that extend far beyond the final performance.

Petrichor stands as a reflection of our commitment to excellence, demonstrating how thoughtful planning and flawless execution can elevate a meaningful initiative into an unforgettable experience. It represents our passion for creating events that not only entertain but also inspire, connect communities and leave a lasting impact.

Organized by: AIRA Foundation
ManagingPartner: Elite Eventz"
            images={{
              main: "/flagship/petrichor/main.jpg",
              photo1: "/flagship/petrichor/photo-1.jpeg",
              photo2: "/flagship/petrichor/photo-2.jpeg",
              photo3: "/flagship/petrichor/photo-3.jpeg",
            }}
          />
          <FlagshipBlock
            id="paratwari"
            name="Paratwari"
            tagline="A Maharashtra Movement"
            description="Paratwari

A Celebration of Culture, Devotion & Musical Excellence

Paratwari is a prestigious cultural and devotional music festival that celebrates the timeless legacy of Indian classical and devotional music. Hosted across seven cities, including Pune, PCMC, Mumbai, Thane, Kolhapur and Solapur, the festival has become a cherished platform for preserving Maharashtra's rich musical heritage while bringing together communities through unforgettable performances. Over the years, Paratwari has featured some of the country's most celebrated artists, including Pt. Shaunak Abhisheki, Aarya Ambekar, Ketakee Mateygaonkar, Bharat Balavalli, Manjusha Patil, Dhananjay Joshi and many more, creating an extraordinary cultural experience that resonates with audiences of all generations.

As the Managing Partner, Elite Eventz played a key role in delivering the festival with precision, professionalism and seamless execution across multiple cities. From strategic planning, venue management, production, artist coordination, logistics, sponsorship support, volunteer management and audience experience to flawless on-ground operations, our team ensured every edition reflected the highest standards of event management while preserving the authenticity and spirit of the festival. Paratwari stands as a testament to Elite Eventz's ability to successfully manage large-scale, multi-city cultural events that honour tradition while delivering world-class experiences."
            mirror
            cityChips
            mainAspect="16/10"
            images={{
              main: "/flagship/paratwari/main.webp",
              photo1: "/flagship/paratwari/photo-1.jpeg",
              photo2: "/flagship/paratwari/photo-2.jpeg",
              photo3: "/flagship/paratwari/photo-3.jpeg",
            }}
          />
        </div>
      </div>
    </section>
  );
}
